import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import UpdatePassword from "@/pages/UpdatePassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import CustomerDashboard from "@/pages/CustomerDashboard";
import ProjectsList from "@/pages/ProjectsList";
import NewProject from "@/pages/NewProject";
import ProjectDetails from "@/pages/ProjectDetails";
import ChatPage from "@/pages/ChatPage";
import AssistantPage from "@/pages/AssistantPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import Billing from "@/pages/Billing";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSection from "@/pages/AdminSection";
import BrainWorkspacePage from "@/pages/BrainWorkspacePage";
import OfficeToolsPage from "@/pages/OfficeToolsPage";

/**
 * Protected — server truth wins. This gate uses only the profile.platform_role
 * loaded from public.profiles. If the profile is unavailable, the user is not
 * silently granted any role.
 *
 * roles: "customer" | "admin_or_staff" | "admin"
 *   "customer"       → admin/staff/customer all allowed
 *   "admin_or_staff" → admin & staff allowed
 *   "admin"          → admin only
 */
function Protected({ children, roles = "customer" }) {
  const { user, profile, platformRole, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#475569] text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  // Profile row required for any authenticated route.
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center border border-[#DCE3ED] p-8 rounded-xl bg-white">
          <div className="fx-mono-tag mb-2">ACCOUNT PENDING</div>
          <h2 className="text-lg font-semibold tracking-tight">Setting up your profile…</h2>
          <p className="mt-2 text-sm text-[#475569]">If this persists, please refresh or contact support. No permissions are granted until your profile row exists.</p>
        </div>
      </div>
    );
  }

  const isAdmin = platformRole === "admin";
  const isStaff = platformRole === "staff";
  if (roles === "admin" && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (roles === "admin_or_staff" && !(isAdmin || isStaff)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Customer portal (also accessible to admin/staff) */}
          <Route path="/dashboard" element={<Protected><CustomerDashboard /></Protected>} />
          <Route path="/dashboard/projects" element={<Protected><ProjectsList /></Protected>} />
          <Route path="/dashboard/projects/:id" element={<Protected><ProjectDetails /></Protected>} />
          <Route path="/dashboard/new" element={<Protected><NewProject /></Protected>} />
          <Route path="/dashboard/chat" element={<Protected><ChatPage /></Protected>} />
          <Route path="/dashboard/assistant" element={<Protected><AssistantPage /></Protected>} />
          <Route path="/dashboard/knowledge-base" element={<Protected><KnowledgeBasePage /></Protected>} />
          <Route path="/dashboard/office-tools" element={<Protected><OfficeToolsPage /></Protected>} />
          <Route path="/dashboard/billing" element={<Protected><Billing /></Protected>} />
          <Route path="/dashboard/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/dashboard/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/dashboard/settings" element={<Protected><Settings /></Protected>} />

          {/* Admin portal — admin + staff */}
          <Route path="/admin" element={<Protected roles="admin_or_staff"><AdminDashboard /></Protected>} />
          <Route path="/admin/projects" element={<Protected roles="admin_or_staff"><AdminDashboard /></Protected>} />
          <Route path="/admin/brain" element={<Protected roles="admin"><BrainWorkspacePage /></Protected>} />
          <Route path="/admin/:section" element={<Protected roles="admin_or_staff"><AdminSection /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
